import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import type { IconNamesType } from 'ksnm-common-ui/lib/Icon/types';

import { designDrawerStore } from '@stores/designDrawerStore';
import { userStore } from '@stores/userStore';

import styles from './DesignInnerMenu.module.scss';

interface IInnerMenu {
  menu: { key: string; name: string; icon: IconNamesType; disabled?: boolean; className?: string }[];
  onSelect: (key: string) => void;
  selected?: string;
}

const DesignInnerMenuComponent = ({ menu, onSelect, selected }: IInnerMenu): JSX.Element => {
  const { form, info: infoPanel, errors, isShowInfo } = designDrawerStore;

  const {
    config: { sideMenuSize },
    changeMenuSizeMode,
  } = userStore;

  const handleSelect = useCallback(
    (info) => {
      onSelect(info);
    },
    [onSelect],
  );

  const fullSizeMode = sideMenuSize === 'large';

  return (
    <div
      className={cm(
        'designInnerMenu',
        { [styles.iconMode]: !fullSizeMode, [styles.textAreaField]: form !== null },
        styles.wrapper,
      )}
    >
      <div className={styles.rounded} />
      <aside className={cm(styles.menu, { [styles.textAreaField]: form !== null })}>
        <div
          className={styles.closeButton}
          role="button"
          tabIndex={-1}
          onClick={changeMenuSizeMode}
          onKeyDown={changeMenuSizeMode}
        >
          <Icon name={fullSizeMode ? 'arrowToRight' : 'arrowToLeft'} noFill />
        </div>
        {fullSizeMode ? <div>{isShowInfo ? infoPanel : <div />}</div> : <div />}
        <div className={styles.menuButtons}>
          {menu.map(({ key, name, icon, disabled, className }) => (
            <div
              className={cm(className, { [styles.active]: selected === key, [styles.disabled]: disabled })}
              tabIndex={-1}
              role="button"
              onClick={() => handleSelect(key)}
              onKeyPress={() => handleSelect(key)}
              key={key}
            >
              {selected === key && fullSizeMode && (
                <>
                  <div className={styles.upperWhite} />
                  <div className={styles.upperBlue} />
                  <div className={styles.downWhite} />
                  <div className={styles.downBlue} />
                </>
              )}
              {errors[key] && fullSizeMode && <Icon name="warning" className={styles.errorIcon} />}
              {fullSizeMode ? (
                <span title={name}>{name}</span>
              ) : (
                <Icon name={icon} className={cm({ [styles.error]: errors[key], [styles.disabledIcon]: disabled })} />
              )}
              {!fullSizeMode && <div className={styles.yellowLine} />}
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
};

export const DesignInnerMenu = observer(DesignInnerMenuComponent);
